import React, { useCallback, useState } from 'react';
import { Button, Modal, Progress } from 'antd';
import { PlusCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { useUpdateEffect } from 'ahooks';
import { useDropzone } from 'react-dropzone';
import warningIcon from '../../assets/images/warning.svg';
import deleteIcon from '../../assets/images/delete-icon.svg';
import { ServiceNameEnum } from '../../enums/serviceName';
import useServices from '../../hooks/useServices';
import AlumlyButton from '../AlumlyButton';

export enum FileType {
  PNG = 'image/png',
  JPEG = 'image/jpeg',
}
export interface Dimension {
  width: number;
  height: number;
}

export interface MyFile {
  key: string,
  id: number | null,
  type: string,
  name: string,
  size: number,
  preview: string,
  dimension: Dimension,
  file?: File,
  uploading?: boolean,
}

export interface UploadError {
  message: string;
}

export interface UploadResponse {
  id: number;
  presignUrl: string;
}

export interface StyleProps {
  width: number;
  height: number;
}

export interface DropZoneProps {
  width: number;
  height: number;
  maxFile: number;
  maxFileSize: number;
  allowFileTypes: FileType[];
  allowDimension?: Dimension | null;
  canDuplicate?: boolean;
  onChange: Function;
  defaultValue?: MyFile[];
  err: any;
}

const DropZoneDefaultProps = {
  allowDimension: null,
  canDuplicate: false,
  defaultValue: [],
};

const bytesToSize = (bytes: number): string => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = parseInt(`${Math.floor(Math.log(bytes) / Math.log(1024))}`, 10);
  return `${Math.round(bytes / 1024 ** i)} ${sizes[i]}`;
};

const fileKey = (file: File | MyFile, index?: number): string => {
  if (index !== undefined) {
    return `${file.name}_${file.size}_${index}`;
  }
  return `${file.name}_${file.size}`;
};

export const getDimension = (url: string): Promise<Dimension> => (
  new Promise((resolve) => {
    const img = new Image();
    img.onload = function call() {
      resolve({
        width: img.width,
        height: img.height,
      });
    };
    img.src = url;
  })
);

export const transformFile = async (files: File[]): Promise<MyFile[]> => {
  const myFiles: MyFile[] = [];

  const totalFile = files.length;
  for (let i = 0; i < totalFile; i += 1) {
    const file = files[i];
    const preview = URL.createObjectURL(file);
    // eslint-disable-next-line no-await-in-loop
    const dimension = await getDimension(preview);
    myFiles.push({
      key: fileKey(file, i),
      id: null,
      type: file.type,
      name: file.name,
      size: file.size,
      preview,
      dimension,
      file,
    });
  }
  return myFiles;
};

const DropZone: React.FC<DropZoneProps> = (props: DropZoneProps) => {
  const {
    width, height, canDuplicate, onChange, defaultValue,
    maxFile, maxFileSize, allowFileTypes, allowDimension,
    err,
  } = props;

  const [files, setFiles] = useState<MyFile[]>(defaultValue ?? []);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<UploadError | null>(null);

  const rootStyle: React.CSSProperties = {
    background: '#F4F4F4',
    border: `1px solid ${err ? 'red' : '#CCD1DD'}`,
    borderRadius: 4,
    padding: 16,
    display: 'inline-flex',
    flexWrap: 'wrap',
    gap: 16,
    width: '100%',
  };

  const cardPreviewStyle: React.CSSProperties = {
    width,
    height,
    border: '1px solid #CCD1DD',
    borderRadius: 4,
    padding: 8,
    position: 'relative',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  };

  const cardUploadStyle: React.CSSProperties = {
    width,
    height,
    border: '1px solid #CCD1DD',
    borderRadius: 4,
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    color: '#6B6F7C',
    fontSize: 12,
    cursor: 'pointer',
  };

  const deleteButtonStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 8,
    right: 8,
    border: '1px solid #CCD1DD',
    color: '#03030C',
    background: '#fff',
    borderRadius: 8,
  };

  const onDelete = (index: number): void => {
    setDeleteIndex(index);
    setShowConfirmDelete(true);
  };

  const cancelDelete = (): void => {
    setShowConfirmDelete(false);
  };

  const confirmedDelete = (): void => {
    setShowConfirmDelete(false);
    const newFiles = files.filter((_, index) => index !== deleteIndex);
    setFiles(newFiles);
  };

  const clearUploadError = (): void => {
    setUploadError(null);
  };

  const { service: uploadFileService } = useServices(`${ServiceNameEnum.file.upload}`);

  const upload = (file: MyFile): Promise<UploadResponse> => new Promise((resolve, reject) => {
    const payload = new FormData();
    payload.append('file', file.file!);
    payload.append('sourceModel', 'course');

    uploadFileService.post(payload)
      .then(({ data, error }) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
  });

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    let dropNewFiles = [...await transformFile(acceptedFiles)].map((file) => ({
      ...file,
      uploading: true,
    }));

    if (!canDuplicate) {
      // filter duplicate file
      dropNewFiles = [...dropNewFiles]
        .filter((file) => {
          if (files.findIndex((f) => fileKey(f) === fileKey(file)) === -1) {
            return file;
          }
          setUploadError({ message: 'Cannot upload duplicate image' });
          return 0;
        });
    }

    const overSizeFiles = [...dropNewFiles].filter((file) => file.size > maxFileSize);
    if (overSizeFiles.length > 0) {
      setUploadError({ message: `Upload file size than ${bytesToSize(maxFileSize)}` });
      return;
    }

    if (allowDimension) {
      const incorrectDimensionFiles = [...dropNewFiles].filter((file) =>
        file.dimension.width !== allowDimension.width || file.dimension.height !== allowDimension.height);
      if (incorrectDimensionFiles.length > 0) {
        setUploadError({
          message: `Upload file resolution is wrong W ${allowDimension.width}x H ${allowDimension.height} px`,
        });
        return;
      }
    }

    const disallowTypeFiles = [...dropNewFiles].filter((file) => (
      !allowFileTypes.map((type) => type.toUpperCase()).includes(file.type.toUpperCase())
    ));
    if (disallowTypeFiles.length > 0) {
      setUploadError({
        message: `Upload file type allow only ${allowFileTypes.map((type) => type.toUpperCase()).join(',')}`,
      });
      return;
    }

    // set new files
    const newFiles = [...files, ...dropNewFiles].map((file, index) => ({
      ...file,
      key: fileKey(file, index),
    }));

    if (maxFile && newFiles.length > maxFile) {
      setUploadError({ message: `Upload more than ${maxFile} file` });
      return;
    }

    setFiles(newFiles);

    newFiles.forEach((file, index) => {
      if (file.uploading) {
        upload(file)
          .then((res) => {
            const uploadedFiles = [...newFiles];
            uploadedFiles[index].id = res.id;
            uploadedFiles[index].preview = res.presignUrl;
            uploadedFiles[index].uploading = false;
            setFiles(uploadedFiles);
          });
      }
    });
  }, [files]);
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
  });

  useUpdateEffect(() => {
    if (typeof onChange === 'function') {
      onChange(files);
    }
  }, [files]);

  return (
    <div style={rootStyle}>
      {
        files.map((file, index) => (
          <div
            key={file.key}
            style={{ ...cardPreviewStyle, backgroundImage: `url('${file.preview}')` }}
          >
            {
              file.uploading && (
                <Progress percent={100} status="active" showInfo={false} />
              )
            }
            {
              !file.uploading && (
                <Button
                  aria-label="delete"
                  type="text"
                  shape="circle"
                  style={deleteButtonStyle}
                  icon={<DeleteOutlined />}
                  onClick={() => onDelete(index)}
                />
              )
            }
          </div>
        ))
      }
      {
        (maxFile === null || files.length < maxFile) && (
          <div style={cardUploadStyle} {...getRootProps()}>
            <input aria-label="Upload File" {...getInputProps()} />
            <PlusCircleOutlined style={{ fontSize: 40, color: '#03030C' }} />
            <p>Drop images or Browse</p>
          </div>
        )
      }

      <Modal
        open={showConfirmDelete}
        onCancel={cancelDelete}
        footer={null}
        width={550}
        centered
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 24 }}>
          <img src={deleteIcon} alt="" style={{ width: '70px', height: '70px' }} />
          <h6 style={{ fontWeight: 'bold', fontSize: '1.25rem', textAlign: 'center' }}>
            Are you sure you want to clear this image?
          </h6>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '16px 0',
              width: '100%',
              gap: 16,
            }}
          >
            <AlumlyButton label="rxvWhite" width={150} onClick={cancelDelete}>Cancel</AlumlyButton>
            <AlumlyButton
              label="rxv"
              width={150}
              onClick={confirmedDelete}
            >
              Clear
            </AlumlyButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={uploadError !== null}
        onCancel={clearUploadError}
        footer={null}
        centered
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <img src={warningIcon} alt="" style={{ width: '70px', height: '70px' }} />
          <div style={{ textAlign: 'center' }}>{uploadError?.message ?? ''}</div>
          <AlumlyButton
            label="rxv"
            width={300}
            onClick={clearUploadError}
          >
            Close
          </AlumlyButton>
        </div>
      </Modal>
    </div>
  );
};

DropZone.defaultProps = DropZoneDefaultProps;

export default DropZone;
